import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, TrendingDown, Users, Package, MessageSquare, Eye,
  DollarSign, Calendar, RefreshCw, Download, BarChart3, PieChart,
  Activity, Target, Zap, Clock, CheckCircle, AlertCircle,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminStats() {
  const { API } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('30d'); // 7d, 30d, 90d, all

  useEffect(() => {
    fetchStats();
  }, [period]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch multiple data sources in parallel
      const [usersRes, productsRes, demandesRes, messagesRes] = await Promise.all([
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/products`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/demandes/admin/stats/summary`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        axios.get(`${API}/messages/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);

      const users = usersRes.data || [];
      const products = productsRes.data || [];
      const demandes = demandesRes.data || {};

      // Calculate stats
      const now = new Date();
      const periodDays = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90 : 365;
      const startDate = new Date(now.getTime() - periodDays * 24 * 60 * 60 * 1000);

      const recentUsers = users.filter(u => new Date(u.created_at) >= startDate);
      const recentProducts = products.filter(p => new Date(p.created_at) >= startDate);

      setStats({
        // KPIs principaux
        total_users: users.length,
        new_users: recentUsers.length,
        users_growth: users.length > 0 ? Math.round((recentUsers.length / users.length) * 100) : 0,
        
        total_products: products.length,
        new_products: recentProducts.length,
        products_approved: products.filter(p => p.approval_status === 'approved').length,
        products_pending: products.filter(p => p.approval_status === 'pending').length,
        
        total_demandes: demandes.total || 0,
        new_demandes: demandes.new_today || 0,
        demandes_by_type: demandes.by_type || {},
        demandes_by_status: demandes.by_status || {},
        
        // Métriques business
        conversion_rate: demandes.total > 0 ? Math.round(((demandes.by_status?.conclu || 0) / demandes.total) * 100) : 0,
        avg_response_time: '2h', // Placeholder
        
        // Par type d'annonce
        by_listing_type: {
          neuf: products.filter(p => p.listing_type === 'neuf').length,
          occasion: products.filter(p => p.listing_type === 'occasion').length,
          location: products.filter(p => p.listing_type === 'location' || p.rental_available).length,
          reconditionne: products.filter(p => p.listing_type === 'reconditionne').length,
        },
        
        // Top catégories
        featured_count: products.filter(p => p.featured).length,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'blue' }) => (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">{title}</p>
            <p className="text-3xl font-bold text-[#0A2540]">{value}</p>
            {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 rounded-xl bg-${color}-100 flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
        </div>
        {trend && (
          <div className={`flex items-center gap-1 mt-3 text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            <span>{trendValue}</span>
          </div>
        )}
      </CardContent>
      <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-${color}-400 to-${color}-600`} />
    </Card>
  );

  const ProgressBar = ({ label, value, max, color = 'blue' }) => {
    const percentage = max > 0 ? Math.round((value / max) * 100) : 0;
    return (
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600">{label}</span>
          <span className="font-medium">{value} ({percentage}%)</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`bg-${color}-500 h-2 rounded-full transition-all duration-500`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-[#635BFF]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50 p-6" data-testid="admin-stats-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2540]">Tableau de bord</h1>
            <p className="text-gray-500">Vue d'ensemble de l'activité GEOTS</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <div className="flex bg-white rounded-lg border p-1">
              {[
                { value: '7d', label: '7 jours' },
                { value: '30d', label: '30 jours' },
                { value: '90d', label: '90 jours' },
                { value: 'all', label: 'Tout' },
              ].map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className={`px-4 py-2 rounded-md text-sm transition-colors ${
                    period === p.value 
                      ? 'bg-[#635BFF] text-white' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Button variant="outline" onClick={fetchStats}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Exporter
            </Button>
          </div>
        </div>

        {/* KPIs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Utilisateurs"
            value={stats?.total_users || 0}
            subtitle={`+${stats?.new_users || 0} nouveaux`}
            icon={Users}
            trend={stats?.new_users > 0 ? 'up' : undefined}
            trendValue={stats?.new_users > 0 ? `+${stats.users_growth}%` : ''}
            color="blue"
          />
          <StatCard
            title="Annonces"
            value={stats?.total_products || 0}
            subtitle={`${stats?.products_approved || 0} en ligne`}
            icon={Package}
            trend="up"
            trendValue={`+${stats?.new_products || 0}`}
            color="green"
          />
          <StatCard
            title="Demandes (Leads)"
            value={stats?.total_demandes || 0}
            subtitle={`${stats?.new_demandes || 0} aujourd'hui`}
            icon={MessageSquare}
            trend={stats?.new_demandes > 0 ? 'up' : undefined}
            trendValue={stats?.new_demandes > 0 ? 'Actif' : ''}
            color="purple"
          />
          <StatCard
            title="Taux de conversion"
            value={`${stats?.conversion_rate || 0}%`}
            subtitle="Demandes → Conclus"
            icon={Target}
            color="amber"
          />
        </div>

        {/* Second Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Pipeline Demandes */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#635BFF]" />
                Pipeline des demandes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <p className="text-3xl font-bold text-blue-600">{stats?.demandes_by_status?.nouveau || 0}</p>
                  <p className="text-sm text-blue-600">Nouveaux</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-xl">
                  <p className="text-3xl font-bold text-yellow-600">{stats?.demandes_by_status?.contacte || 0}</p>
                  <p className="text-sm text-yellow-600">Contactés</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-xl">
                  <p className="text-3xl font-bold text-orange-600">{stats?.demandes_by_status?.en_cours || 0}</p>
                  <p className="text-sm text-orange-600">En cours</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <p className="text-3xl font-bold text-green-600">{stats?.demandes_by_status?.conclu || 0}</p>
                  <p className="text-sm text-green-600">Conclus</p>
                </div>
              </div>
              
              {/* By Type */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-medium text-[#0A2540] mb-4">Par type de demande</h4>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {[
                    { key: 'vente', label: 'Vente', color: 'blue' },
                    { key: 'location', label: 'Location', color: 'purple' },
                    { key: 'service', label: 'Service', color: 'green' },
                    { key: 'etude', label: 'Études', color: 'cyan' },
                    { key: 'travaux', label: 'Travaux', color: 'amber' },
                    { key: 'recrutement', label: 'Recrutement', color: 'rose' },
                  ].map((type) => (
                    <div key={type.key} className={`text-center p-3 bg-${type.color}-50 rounded-lg`}>
                      <p className={`text-xl font-bold text-${type.color}-600`}>
                        {stats?.demandes_by_type?.[type.key] || 0}
                      </p>
                      <p className="text-xs text-gray-500">{type.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Annonces par type */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="w-5 h-5 text-[#635BFF]" />
                Répartition annonces
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProgressBar 
                label="Occasion" 
                value={stats?.by_listing_type?.occasion || 0} 
                max={stats?.total_products || 1}
                color="amber"
              />
              <ProgressBar 
                label="Neuf" 
                value={stats?.by_listing_type?.neuf || 0} 
                max={stats?.total_products || 1}
                color="blue"
              />
              <ProgressBar 
                label="Location" 
                value={stats?.by_listing_type?.location || 0} 
                max={stats?.total_products || 1}
                color="purple"
              />
              <ProgressBar 
                label="Reconditionné" 
                value={stats?.by_listing_type?.reconditionne || 0} 
                max={stats?.total_products || 1}
                color="green"
              />
              
              <div className="mt-6 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">En vedette</span>
                  <Badge className="bg-amber-100 text-amber-800">
                    ⭐ {stats?.featured_count || 0}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Third Row - Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Annonces en attente */}
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">En attente de validation</p>
                  <p className="text-2xl font-bold text-[#0A2540]">{stats?.products_pending || 0}</p>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-4" size="sm">
                Voir les approbations
              </Button>
            </CardContent>
          </Card>

          {/* Temps de réponse */}
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Zap className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Temps de réponse moyen</p>
                  <p className="text-2xl font-bold text-[#0A2540]">{stats?.avg_response_time || '—'}</p>
                </div>
              </div>
              <div className="mt-4 text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Excellent (objectif: &lt; 4h)
              </div>
            </CardContent>
          </Card>

          {/* Actions rapides */}
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold text-[#0A2540] mb-4">Actions rapides</h3>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start" size="sm">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Voir les nouvelles demandes
                </Button>
                <Button variant="outline" className="w-full justify-start" size="sm">
                  <Package className="w-4 h-4 mr-2" />
                  Valider les annonces
                </Button>
                <Button variant="outline" className="w-full justify-start" size="sm">
                  <Users className="w-4 h-4 mr-2" />
                  Gérer les utilisateurs
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
